import { useState, useEffect } from "react";
import { AdminLayout } from "@/pages/admin/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { MessageCircle, TrendingUp, Users, Target, AlertTriangle, HelpCircle, ChevronDown, ChevronUp, Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatbotAdmin() {
  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [showKBModal, setShowKBModal] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, conversationsRes, kbRes] = await Promise.all([
        axios.get(`${API}/admin/chatbot/analytics?days=${days}`, { headers }),
        axios.get(`${API}/admin/chatbot/conversations?days=7`, { headers }),
        axios.get(`${API}/admin/chatbot/knowledge-base`, { headers })
      ]);

      setAnalytics(analyticsRes.data);
      setConversations(conversationsRes.data.conversations || []);
      setKnowledgeBase(kbRes.data.entries || []);
    } catch (error) {
      console.error("Error fetching chatbot data:", error);
      toast.error("Error cargando datos del chatbot");
    } finally {
      setLoading(false);
    }
  };

  const saveKnowledgeBase = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/chatbot/knowledge-base`,
        { entries: knowledgeBase },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Base de conocimiento guardada");
    } catch (error) {
      toast.error("Error guardando");
    }
  };

  const addKBEntry = () => {
    setKnowledgeBase([
      ...knowledgeBase,
      { id: `kb${Date.now()}`, question: "", answer: "", category: "general", active: true }
    ]);
  };

  const updateKBEntry = (index, field, value) => {
    const updated = [...knowledgeBase];
    updated[index][field] = value;
    setKnowledgeBase(updated);
  };

  const removeKBEntry = (index) => {
    setKnowledgeBase(knowledgeBase.filter((_, i) => i !== index));
  };

  const viewConversation = (conv) => {
    setSelectedConversation(conv);
    setShowConversationModal(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Chatbot Analytics</h1>
            <p className="text-stone-500">Métricas de conversión y conversaciones</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowKBModal(true)}>
              <HelpCircle className="w-4 h-4 mr-2" />
              Base de conocimiento
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 rounded-full">
                  <MessageCircle className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Conversaciones</p>
                  <p className="text-2xl font-bold">{analytics?.total_conversations || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Clics en "Agendar"</p>
                  <p className="text-2xl font-bold">{analytics?.clicked_agendar || 0}</p>
                  <p className="text-xs text-stone-400">{analytics?.click_rate || 0}% de conversaciones</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Conversiones</p>
                  <p className="text-2xl font-bold">{analytics?.converted || 0}</p>
                  <p className="text-xs text-stone-400">{analytics?.conversion_rate || 0}% tasa</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Crisis detectadas</p>
                  <p className="text-2xl font-bold">{analytics?.crisis_detected || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Intentions & Objections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Intenciones detectadas</CardTitle>
              <CardDescription>Qué buscan los usuarios</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.intentions && Object.entries(analytics.intentions).map(([intent, count]) => (
                  <div key={intent} className="flex justify-between items-center">
                    <span className="text-sm text-stone-600 capitalize">{intent.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
                {(!analytics?.intentions || Object.keys(analytics.intentions).length === 0) && (
                  <p className="text-stone-400 text-sm">Sin datos aún</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Objeciones detectadas</CardTitle>
              <CardDescription>Qué frena a los usuarios</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.objections && Object.entries(analytics.objections).map(([obj, count]) => (
                  <div key={obj} className="flex justify-between items-center">
                    <span className="text-sm text-stone-600 capitalize">{obj}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
                {(!analytics?.objections || Object.keys(analytics.objections).length === 0) && (
                  <p className="text-stone-400 text-sm">Sin objeciones detectadas</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Questions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preguntas más frecuentes</CardTitle>
            <CardDescription>Lo que más preguntan los usuarios</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.top_questions?.map((q, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-stone-100 last:border-0">
                  <span className="text-sm text-stone-700 truncate max-w-[80%]">"{q.question}"</span>
                  <span className="text-sm text-stone-500">{q.count}x</span>
                </div>
              ))}
              {(!analytics?.top_questions || analytics.top_questions.length === 0) && (
                <p className="text-stone-400 text-sm">Sin preguntas registradas aún</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Conversations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversaciones recientes</CardTitle>
            <CardDescription>Últimos 7 días</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {conversations.map((conv) => (
                <div 
                  key={conv.id}
                  className="p-3 border border-stone-200 rounded-lg hover:bg-stone-50 cursor-pointer"
                  onClick={() => viewConversation(conv)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-stone-800">
                        {conv.listener_name || "Página general"}
                      </p>
                      <p className="text-xs text-stone-500">
                        {conv.messages?.length || 0} mensajes • {new Date(conv.created_at).toLocaleDateString('es')}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {conv.actions?.clicked_agendar && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Clic Agendar</span>
                      )}
                      {conv.crisis_detected && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Crisis</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {conversations.length === 0 && (
                <p className="text-stone-400 text-sm text-center py-4">Sin conversaciones recientes</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversation Modal */}
      <Dialog open={showConversationModal} onOpenChange={setShowConversationModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conversación</DialogTitle>
            <DialogDescription>
              {selectedConversation?.listener_name || "Página general"} • {selectedConversation?.messages?.length || 0} mensajes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {selectedConversation?.messages?.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user' ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-800'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Knowledge Base Modal */}
      <Dialog open={showKBModal} onOpenChange={setShowKBModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Base de conocimiento</DialogTitle>
            <DialogDescription>
              Preguntas frecuentes y respuestas para el chatbot
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {knowledgeBase.map((entry, idx) => (
              <div key={entry.id} className="p-4 border border-stone-200 rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <Select value={entry.category} onValueChange={(v) => updateKBEntry(idx, 'category', v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="pricing">Precios</SelectItem>
                      <SelectItem value="objection">Objeción</SelectItem>
                      <SelectItem value="faq">FAQ</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={entry.active} 
                      onCheckedChange={(v) => updateKBEntry(idx, 'active', v)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeKBEntry(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <Input 
                  placeholder="Pregunta..."
                  value={entry.question}
                  onChange={(e) => updateKBEntry(idx, 'question', e.target.value)}
                />
                <Textarea 
                  placeholder="Respuesta..."
                  value={entry.answer}
                  onChange={(e) => updateKBEntry(idx, 'answer', e.target.value)}
                  rows={2}
                />
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" onClick={addKBEntry} className="flex-1">
                <Plus className="w-4 h-4 mr-2" /> Agregar entrada
              </Button>
              <Button onClick={saveKnowledgeBase} className="flex-1">
                <Save className="w-4 h-4 mr-2" /> Guardar cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
